/**
 * Audit sub-page 공통 좌측 아이콘 사이드바 (48px wide).
 *
 * 메뉴 권한 연동: 일반 사용자는 메인 메뉴와 동일 기준(menuConfig.hiddenByDefault
 * + 서버 visibility)으로 노출 메뉴만 — 기본 딥러닝(메인)/AI HS분류/관세청 품목분류
 * 사례 → audit 항목은 AI HS분류·관세청 품목분류 사례 + Home 3개. 관리자
 * (MANAGER_YN='Y')는 전체 노출(개발/관리 페이지 포함).
 *
 * - always: 권한 무관 항상 표시 (Home)
 * - menuKey: menuConfig id 와 매핑 — visibility/hiddenByDefault 기준 노출
 * - adminOnly: menuConfig 에 없는 개발/관리 페이지 — 관리자만
 */
import React, { useState, useEffect } from "react";
import {
  Home,
  LayoutGrid,
  Sparkles,
  Search,
  Database,
  BookOpen,
  Building2,
  Info,
  TrendingUp,
  GitBranch,
  Upload,
  ShieldCheck,
  ListChecks,
} from "lucide-react";
import { MENU_ITEMS } from "../config/menuConfig";

const NAV = [
  { icon: Home, title: "docuai 메인", href: "/", always: true },
  {
    icon: Info,
    title: "시스템 소개",
    href: "/?audit=onto-overview",
    match: "onto-overview",
    adminOnly: true,
  },
  {
    icon: LayoutGrid,
    title: "HS 분류 Hub",
    href: "/?audit=hs-hub",
    match: "hs-hub",
    menuKey: "hs-hub",
  },
  {
    icon: Sparkles,
    title: "AI 분류",
    href: "/?audit=precedents&open=ai",
    match: "precedents-ai",
    adminOnly: true,
  },
  {
    icon: Search,
    title: "HS 통합 검색",
    href: "/?audit=main",
    match: "main",
    menuKey: "hs-unified-search",
  },
  {
    icon: Database,
    title: "관세청 품목분류 사례",
    href: "/?audit=precedents",
    match: "precedents",
    menuKey: "hs-precedents-explorer",
  },
  {
    icon: BookOpen,
    title: "동의어 사전",
    href: "/?audit=synonyms",
    match: "synonyms",
    menuKey: "hs-synonyms-admin",
  },
  {
    icon: Building2,
    title: "관세사 거래처",
    href: "/?audit=trade-partners",
    match: "trade-partners",
    adminOnly: true,
  },
  {
    icon: TrendingUp,
    title: "피드백 학습 대시보드",
    href: "/?audit=feedback-dashboard",
    match: "feedback-dashboard",
    adminOnly: true,
  },
  {
    icon: GitBranch,
    title: "관세율표",
    href: "/?audit=quickref&noextra=1", // 속견표 그리드 + 좌측 아코디언(전용 3버튼 숨김)
    match: "quickref",
    menuKey: "hs-ai-classify",
  },
  {
    icon: Upload,
    title: "인보이스 업로드 분류",
    href: "/?audit=invoice-upload",
    match: "invoice-upload",
    adminOnly: true,
  },
  {
    icon: ListChecks,
    title: "대량 세번분류",
    href: "/?audit=bulk-classify",
    match: "bulk-classify",
    menuKey: "hs-bulk-classify",
  },
  {
    icon: ShieldCheck,
    title: "Classification Master (verified)",
    href: "/?audit=onto-master",
    match: "onto-master",
    adminOnly: true,
  },
];

// menuConfig id → hiddenByDefault 매핑 (서버 visibility 없을 때 fallback)
const HIDDEN_BY_DEFAULT = Object.fromEntries(
  MENU_ITEMS.map((m) => [m.id, m.hiddenByDefault]),
);

function apiBase() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" ? `http://${h}:3002` : "";
}

function currentMatch() {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  const route = p.get("audit");
  if (route === "precedents" && p.get("open") === "ai") return "precedents-ai";
  return route;
}

export default function AuditSideNav() {
  const cur = currentMatch();
  const [vis, setVis] = useState(null); // { isManager, visibility }

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setVis({ isManager: false, visibility: {} });
      return;
    }
    fetch(`${apiBase()}/api/auth/menu-visibility`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.success) {
          setVis({ isManager: !!d.isManager, visibility: d.visibility || {} });
        } else {
          setVis({ isManager: false, visibility: {} });
        }
      })
      .catch(() => setVis({ isManager: false, visibility: {} }));
  }, []);

  const isManager = vis?.isManager;
  const vmap = vis?.visibility || {};
  const visibleNav = NAV.filter((n) => {
    if (n.always) return true;
    if (isManager) return true;
    if (n.adminOnly) return false;
    if (n.menuKey) {
      if (Object.prototype.hasOwnProperty.call(vmap, n.menuKey)) {
        return vmap[n.menuKey] === true;
      }
      return !HIDDEN_BY_DEFAULT[n.menuKey]; // 서버 미설정 → 코드 디폴트
    }
    return false;
  });

  return (
    <nav
      style={{
        width: 48,
        flexShrink: 0,
        borderRight: "1px solid var(--audit-border)",
        background: "var(--audit-bg-elevated)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "8px 0",
        gap: 4,
        minHeight: "100vh",
      }}
    >
      {visibleNav.map((n) => {
        const Icon = n.icon;
        const active = cur === n.match;
        return (
          <a
            key={n.title}
            href={n.href}
            title={n.title}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: active ? "var(--audit-accent)" : "var(--audit-text-muted)",
              background: active ? "var(--audit-accent-bg)" : "transparent",
              border: active
                ? "1px solid var(--audit-accent)"
                : "1px solid transparent",
              textDecoration: "none",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              if (!active) {
                e.currentTarget.style.background = "var(--audit-border)";
                e.currentTarget.style.color = "var(--audit-text)";
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--audit-text-muted)";
              }
            }}
          >
            <Icon size={18} />
          </a>
        );
      })}
    </nav>
  );
}
